import { FormEvent, Fragment, useEffect, useState, type CSSProperties } from "react";
import { TableSkeleton } from "../../components/Loading";
import { Pager } from "../../components/Pager";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deliveriesApi,
  type Delivery,
  type DeliveryStatus,
  type SupplierRejection,
  type SupplierRejectionTally,
} from "../../api/deliveries";
import {
  procurementContractsApi,
  procurementSuppliersApi,
  type ProcurementContract,
  type ProcurementSupplier,
} from "../../api/procurementContracts";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import { DeliveryStatusBadge } from "../../components/DeliveryStatusBadge";
import { DeliveryNoContractTag } from "../../components/DeliveryNoContractTag";
import Ic from "../../components/icons.jsx";
import { DeliveriesBoard } from "./DeliveriesBoardPage";
import { ExportButton } from "../../components/ExportButton";

const STATUSES: DeliveryStatus[] = [
  "ANNOUNCED",
  "CONFIRMED",
  "SAMPLED",
  "RECEIVED",
  "SETTLED",
  "LAB_REJECTED",
  "REJECTED",
  "CANCELLED",
];

/**
 * Isporuke i kvalitet (deliveries) list — the inbound counterpart of
 * OrdersPage. Suppliers see only their own company's deliveries (RLS); Otkup
 * / Kvalitet / Admin staff can filter across companies. The announcement is
 * Bankom's intake order: the otkup desk opens it here for one of its
 * suppliers, and the supplier only ever reads it.
 */
export default function DeliveriesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { state, hasPermission } = useAuth();

  const [items, setItems] = useState<Delivery[] | null>(null);
  // Strana, ukupan broj i to da li čitanje traje. Spisak isporuka raste sa svakim kamionom — istorija
  // prijema iz starog ERP-a je 22.182 reda — pa se ne povlači ceo. `ucitava` se drži odvojeno od
  // praznog spiska: prazno pri prvom otvaranju je kostur, a prelazak na drugu stranu ostavlja zatečene
  // redove na ekranu, inače tabela trepne pri svakom kliku i čitalac izgubi mesto na kom je bio.
  const [strana, setStrana] = useState(0);
  const [ukupno, setUkupno] = useState(0);
  const [ucitava, setUcitava] = useState(false);
  const VELICINA = 25;
  const [statusFilter, setStatusFilter] = useState<DeliveryStatus | "">("");
  // Firma se prima iz adrese, pa veza sa kartice dobavljača stvarno suzi spisak. Bez ovoga
  // `?companyId=` bi stajao u adresi a ekran bi ga ignorisao — veza koja obećava filter koji
  // se ne primenjuje gora je od veze bez filtera.
  const [companyFilter, setCompanyFilter] = useState<string>(
    () => new URLSearchParams(window.location.search).get("companyId") ?? "",
  );
  const [companies, setCompanies] = useState<ProcurementSupplier[]>([]);
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [query, setQuery] = useState("");
  // The chosen view survives a reload; a person who works in the board does not
  // want to switch back to the list on every visit.
  const [view, setView] = useState<"list" | "board">(
    () => (localStorage.getItem("deliveries.view") === "board" ? "board" : "list"),
  );

  const isInternal = state.status === "authenticated" && state.user.internal;
  const canAnnounce = hasPermission("deliveries:announce");

  async function load() {
    setUcitava(true);
    try {
      const r = await deliveriesApi.list({
        status: statusFilter || undefined,
        companyId: companyFilter || undefined,
        from: from || undefined,
        to: to || undefined,
        q: query.trim() || undefined,
      }, strana, VELICINA);
      setItems(r.content);
      setUkupno(r.totalElements);
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setUcitava(false);
    }
  }

  useEffect(() => { void load(); }, [statusFilter, companyFilter, from, to, query, strana]);

  // Svaka promena filtera vraća na prvu stranu. Bez ovoga filter koji suzi spisak na dve strane, dok
  // se stoji na petoj, daje prazan ekran koji izgleda kao da pogodaka nema.
  useEffect(() => { setStrana(0); }, [statusFilter, companyFilter, from, to, query]);

  useEffect(() => { localStorage.setItem("deliveries.view", view); }, [view]);

  // Supplier filter is internal-only. The list comes from the otkup-scoped
  // supplier endpoint, so it holds exactly the suppliers whose deliveries this
  // page can show. Best-effort: a failure hides the filter, never the list.
  useEffect(() => {
    if (!isInternal) return;
    void procurementSuppliersApi.list().then(setCompanies).catch(() => undefined);
  }, [isInternal]);

  // Built once: the same filters feed both exports, and the inline version was
  // already spelled out four times inside a single button.
  const exportQuery = (() => {
    const usp = new URLSearchParams(
      Object.entries({ status: statusFilter, companyId: companyFilter }).filter(([, v]) => v) as [
        string,
        string,
      ][],
    ).toString();
    return usp ? `?${usp}` : "";
  })();

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("deliveries.list.title", "Deliveries")}</h1>
        </div>
        <div className="page-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {isInternal && companies.length > 0 && (
            <Select<string>
              value={companyFilter}
              onChange={setCompanyFilter}
              ariaLabel={t("deliveries.list.allCompanies", "All companies")}
              style={{ minWidth: 200 }}
              options={[
                { value: "", label: t("deliveries.list.allCompanies", "All companies") },
                ...companies.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          )}
          <Select<string>
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as DeliveryStatus | "")}
            ariaLabel={t("deliveries.list.allStatuses", "All statuses")}
            style={{ minWidth: 190 }}
            options={[
              { value: "", label: t("deliveries.list.allStatuses", "All statuses") },
              ...STATUSES.map((s) => ({ value: s, label: t(`deliveries.status.${s.toLowerCase()}`) })),
            ]}
          />
          <div style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: "var(--r-sm)" }}>
            <button
              type="button"
              className={view === "list" ? "btn sm primary" : "btn sm"}
              onClick={() => setView("list")}
            >
              {t("deliveries.view.list", "List")}
            </button>
            <button
              type="button"
              className={view === "board" ? "btn sm primary" : "btn sm"}
              onClick={() => setView("board")}
            >
              {t("deliveries.view.board", "Board")}
            </button>
          </div>
          <ExportButton
            path={`/exports/deliveries.csv${exportQuery}`}
            label={t("deliveries.list.exportCsv", "Export CSV")}
            filename="deliveries.csv"
          />
          <ExportButton
            path={`/exports/deliveries.xlsx${exportQuery}`}
            label={t("deliveries.list.exportXlsx", "Export XLSX")}
            filename="deliveries.xlsx"
          />
          {canAnnounce && (
            <button type="button" className="btn primary" onClick={() => setAnnounceOpen(true)}>
              <Ic.plus /> {t("deliveries.action.announce", "Announce delivery")}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
          <span style={{ color: "var(--ink-3)" }}>{t("deliveries.filter.from", "From")}</span>
          <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
          <span style={{ color: "var(--ink-3)" }}>{t("deliveries.filter.to", "To")}</span>
          <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, flex: 1, minWidth: 220 }}>
          <span style={{ color: "var(--ink-3)" }}>{t("deliveries.filter.search", "Search")}</span>
          <input
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("deliveries.filter.searchHint", "Weighbridge slip, receipt number, plate or supplier")}
          />
        </label>
        {(from || to || query || statusFilter || companyFilter) && (
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              setFrom("");
              setTo("");
              setQuery("");
              setStatusFilter("");
              setCompanyFilter("");
            }}
          >
            {t("deliveries.filter.clear", "Clear filters")}
          </button>
        )}
      </div>

      {/* Stranič stoji iznad oba prikaza: i tabla pokazuje jednu stranu, pa bez ovoga izgleda kao da je
          to ceo posao. Vrteška je u njemu, pored broja strane — tamo se klik i desio. */}
      <Pager page={strana} size={VELICINA} total={ukupno} busy={ucitava} onPage={setStrana} />

      <div
        style={{
          opacity: ucitava && items ? 0.55 : 1,
          pointerEvents: ucitava && items ? "none" : undefined,
          transition: "opacity 120ms",
        }}
      >
      {view === "board" && items ? (
        <DeliveriesBoard deliveries={items} />
      ) : (
      <section style={card}>
        {!items ? (
          <TableSkeleton rows={6} cols={6} label={t("common.loading")} />
        ) : items.length === 0 ? (
          <div style={emptyState}>
            <p style={{ margin: 0, color: "var(--ink-3)" }}>{t("deliveries.list.empty", "No deliveries.")}</p>
          </div>
        ) : (
          <table className="list-card" style={tbl}>
            <thead>
              <tr>
                {isInternal && <th style={th}>{t("deliveries.list.col.company", "Company")}</th>}
                <th style={th}>{t("deliveries.list.col.announcedDate", "Announced")}</th>
                <th style={th}>{t("deliveries.list.col.receivedOn", "Primljeno")}</th>
                <th style={th}>{t("deliveries.list.col.estimatedQuantity", "Est. quantity (t)")}</th>
                <th style={th}>{t("deliveries.list.col.netKg", "Net (kg)")}</th>
                <th style={th}>{t("deliveries.list.col.status", "Status")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => navigate(`/procurement/deliveries/${d.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  {isInternal && (
                    <td style={td} data-label={t("deliveries.list.col.company", "Company")}>
                      <Link to={`/procurement/deliveries/${d.id}`} style={rowLink} onClick={(e) => e.stopPropagation()}>
                        {d.companyName}
                      </Link>
                    </td>
                  )}
                  {/* Najava i prijem su dva datuma i stoje odvojeno. Uvezen prijem najavu nema — za
                      22.182 kamiona iz starog ERP-a ona postoji samo za manji deo — pa prazno znači
                      „nije najavljivano", a ne da datum nedostaje. */}
                  <td style={td} data-label={t("deliveries.list.col.announcedDate", "Announced")}>
                    {d.announcedDate == null ? (
                      <span style={{ color: "var(--ink-4)" }}>—</span>
                    ) : !isInternal ? (
                      <Link to={`/procurement/deliveries/${d.id}`} style={rowLink} onClick={(e) => e.stopPropagation()}>
                        {formatDate(d.announcedDate, i18n.resolvedLanguage)}
                      </Link>
                    ) : (
                      formatDate(d.announcedDate, i18n.resolvedLanguage)
                    )}
                  </td>
                  <td style={td} data-label={t("deliveries.list.col.receivedOn", "Primljeno")}>
                    {d.receivedOn == null ? (
                      <span style={{ color: "var(--ink-4)" }}>—</span>
                    ) : (
                      formatDate(d.receivedOn, i18n.resolvedLanguage)
                    )}
                  </td>
                  <td style={td} data-label={t("deliveries.list.col.estimatedQuantity", "Est. quantity (t)")}>
                    {d.estimatedQuantityT ?? <span style={{ color: "var(--ink-4)" }}>—</span>}
                  </td>
                  <td style={td} data-label={t("deliveries.list.col.netKg", "Net (kg)")}>{d.netKg ?? "—"}</td>
                  <td style={td} data-label={t("deliveries.list.col.status", "Status")}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <DeliveryStatusBadge status={d.status} />
                      <DeliveryNoContractTag delivery={d} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      )}
      </div>

      {/* I ispod spiska, da dugme ne ostane van vidokruga kad se prođe strana. */}
      <Pager page={strana} size={VELICINA} total={ukupno} busy={ucitava} onPage={setStrana} />

      {isInternal && <SupplierRejectionsPanel />}

      {announceOpen && (
        <AnnounceDeliveryDialog
          onClose={() => setAnnounceOpen(false)}
          onCreated={async (created) => {
            setAnnounceOpen(false);
            await load();
            navigate(`/procurement/deliveries/${created.id}`);
          }}
        />
      )}
    </div>
  );
}

/**
 * Refusals per supplier — the part of a supplier's quality record the portal can
 * honestly show.
 *
 * <p>There is no tonnage column and there will not be one: a refused load is turned
 * back before the weighbridge, so nothing about it was ever weighed. The count and the
 * date of the last refusal are the whole of what is known, and that is already the
 * difference between a co-operative whose loads are turned back three times a season and
 * one whose never are.
 */
function SupplierRejectionsPanel() {
  const { t, i18n } = useTranslation();
  const [tally, setTally] = useState<SupplierRejectionTally[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [rows, setRows] = useState<SupplierRejection[]>([]);

  useEffect(() => {
    // Best effort: a caller who may not read the register simply sees no panel.
    void deliveriesApi.rejectionTally().then(setTally).catch(() => setTally([]));
  }, []);

  async function toggle(companyId: string) {
    if (open === companyId) {
      setOpen(null);
      return;
    }
    setOpen(companyId);
    setRows(await deliveriesApi.rejectionsForSupplier(companyId).catch(() => []));
  }

  if (!tally || tally.length === 0) return null;

  return (
    <section className="card">
      <div className="card-h">
        <h2 style={{ margin: 0, fontSize: 14 }}>
          {t("deliveries.rejections.title", "Refused loads by supplier")}
        </h2>
        <span className="muted" style={{ fontSize: 11.5 }}>
          {t(
            "deliveries.rejections.note",
            "A refused load is turned back before the weighbridge, so there is no quantity to show — only that it happened.",
          )}
        </span>
      </div>
      <div className="card-b">
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("deliveries.rejections.col.supplier", "Supplier")}</th>
              <th>{t("deliveries.rejections.col.count", "Refusals")}</th>
              <th>{t("deliveries.rejections.col.last", "Last one")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tally.map((r) => (
              <Fragment key={r.companyId}>
                <tr>
                  <td data-label={t("deliveries.rejections.col.supplier", "Supplier")}>
                    {r.companyName ?? r.companyId}
                  </td>
                  <td data-label={t("deliveries.rejections.col.count", "Refusals")}>
                    <span className={r.rejections > 2 ? "tag err" : "tag warn"}>{r.rejections}</span>
                  </td>
                  <td data-label={t("deliveries.rejections.col.last", "Last one")}>
                    {r.lastRejectedOn ? formatDate(r.lastRejectedOn, i18n.resolvedLanguage) : "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="btn sm ghost"
                      aria-expanded={open === r.companyId}
                      onClick={() => void toggle(r.companyId)}
                    >
                      {open === r.companyId
                        ? t("action.hide", "Hide")
                        : t("deliveries.rejections.show", "Show the refusals")}
                    </button>
                  </td>
                </tr>
                {open === r.companyId && (
                  <tr>
                    <td colSpan={4}>
                      {rows.length === 0 ? (
                        <p className="empty">{t("deliveries.rejections.empty", "No refusals on record.")}</p>
                      ) : (
                        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                          {rows.map((x) => (
                            <li key={x.id} style={{ fontSize: 13 }}>
                              <b>{formatDate(x.rejectedOn, i18n.resolvedLanguage)}</b>
                              {" · "}
                              {x.culture}
                              {x.vehiclePlate ? ` · ${x.vehiclePlate}` : ""}
                              <div className="muted" style={{ fontSize: 12 }}>{x.reason}</div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Koliko se aktivnih ugovora jednog kooperanta traži za birač — berba ih ne nosi ni blizu toliko. */
const UGOVORA_U_BIRACU = 100;

/**
 * Announce form — the otkup desk (or an admin) opens an intake order for one of
 * its suppliers. The supplier is always chosen here, from the suppliers the
 * caller may act on; the contract list narrows to that supplier's active
 * contracts. The plate and the driver are part of the order — the gate knows the
 * truck by them — and can still be corrected on the detail page until the load
 * is weighed; carrier and trailer are not on every run.
 */
function AnnounceDeliveryDialog({
  onClose, onCreated,
}: {
  onClose: () => void;
  onCreated: (created: Delivery) => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [suppliers, setSuppliers] = useState<ProcurementSupplier[]>([]);
  const [contracts, setContracts] = useState<ProcurementContract[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [contractId, setContractId] = useState("");
  const [announcedDate, setAnnouncedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [estimatedQuantityT, setEstimatedQuantityT] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [trailerPlate, setTrailerPlate] = useState("");
  const [driverName, setDriverName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void procurementSuppliersApi.list({ actable: true })
      .then((list) => {
        setSuppliers(list);
        // One supplier to choose from is no choice: pick it.
        if (list.length === 1) setCompanyId(list[0].id);
      })
      .catch(() => undefined);
  }, []);

  // Ugovori se traže za izabranog kooperanta, u bazi, a ne ceo registar pa odsecanje u pregledaču:
  // jedan kooperant nosi nekoliko ugovora u berbi, a registar ih drži stotinama.
  useEffect(() => {
    if (!companyId) { setContracts([]); return; }
    void procurementContractsApi.list({ status: "ACTIVE", companyId }, 0, UGOVORA_U_BIRACU)
      .then((strana) => setContracts(strana.content))
      .catch(() => undefined);
  }, [companyId]);

  // A contract belongs to one supplier; changing the supplier drops a contract
  // that does not belong to the new one.
  const supplierContracts = contracts.filter((c) => c.companyId === companyId);
  useEffect(() => {
    if (contractId && !supplierContracts.some((c) => c.id === contractId)) setContractId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-evaluate when the supplier changes
  }, [companyId, contracts]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const qty = Number(estimatedQuantityT);
    if (!companyId) { setErr(t("deliveries.field.supplierRequired", "Pick the supplier.")); return; }
    if (!announcedDate) { setErr(t("deliveries.field.announcedDateRequired", "Announced date is required.")); return; }
    if (!(qty > 0)) { setErr(t("deliveries.field.quantityPositive", "Estimated quantity must be > 0.")); return; }
    if (!vehiclePlate.trim() || !driverName.trim()) {
      setErr(t("deliveries.field.transportRequired", "The vehicle plate and the driver are required."));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await deliveriesApi.announce({
        companyId,
        procurementContractId: contractId || undefined,
        announcedDate,
        estimatedQuantityT: qty,
        carrierName: carrierName.trim() || undefined,
        vehiclePlate: vehiclePlate.trim(),
        trailerPlate: trailerPlate.trim() || undefined,
        driverName: driverName.trim(),
      });
      toast.success(t("deliveries.toast.announced", "Delivery announced."));
      await onCreated(created);
    } catch (e2) {
      setErr((e2 as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
          {t("deliveries.action.announce", "Announce delivery")}
        </h3>
        {err && <div style={errBox}>{err}</div>}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label={t("deliveries.field.supplier", "Supplier")}>
            <Select<string>
              value={companyId}
              onChange={setCompanyId}
              ariaLabel={t("deliveries.field.supplier", "Supplier")}
              placeholder={t("deliveries.field.supplierPick", "— Pick the supplier —")}
              style={{ width: "100%" }}
              options={suppliers.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
          <Field label={t("deliveries.field.announcedDate", "Announced date")}>
            <input
              type="date"
              value={announcedDate}
              onChange={(e) => setAnnouncedDate(e.target.value)}
              required
              className="input"
              style={{ width: "100%" }}
            />
          </Field>
          <Field label={t("deliveries.field.estimatedQuantity", "Estimated quantity (t)")}>
            <input
              type="number"
              min="0"
              step="0.001"
              value={estimatedQuantityT}
              onChange={(e) => setEstimatedQuantityT(e.target.value)}
              required
              className="input"
              style={{ width: "100%" }}
            />
          </Field>
          <Field label={t("deliveries.field.contract", "Procurement contract")}>
            <Select<string>
              value={contractId}
              onChange={setContractId}
              ariaLabel={t("deliveries.field.contract", "Procurement contract")}
              disabled={!companyId}
              style={{ width: "100%" }}
              options={[
                { value: "", label: t("deliveries.field.contractNone", "— None —") },
                ...supplierContracts.map((c) => ({ value: c.id, label: c.referenceNumber })),
              ]}
            />
            {companyId && !contractId && (
              <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                {t("deliveries.contract.noneHint", "Outside any procurement contract — a director confirms it.")}
              </span>
            )}
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <Field label={t("deliveries.transport.carrier", "Carrier")}>
              <input value={carrierName} onChange={(e) => setCarrierName(e.target.value)} className="input" style={{ width: "100%" }} />
            </Field>
            <Field label={t("deliveries.transport.driver", "Driver")} required>
              <input
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                required
                className="input"
                style={{ width: "100%" }}
              />
            </Field>
            <Field label={t("deliveries.transport.vehiclePlate", "Vehicle plate")} required>
              <input
                value={vehiclePlate}
                onChange={(e) => setVehiclePlate(e.target.value)}
                required
                className="input"
                style={{ width: "100%" }}
              />
            </Field>
            <Field label={t("deliveries.transport.trailerPlate", "Trailer plate")}>
              <input value={trailerPlate} onChange={(e) => setTrailerPlate(e.target.value)} className="input" style={{ width: "100%" }} />
            </Field>
          </div>
          <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
            {t("deliveries.transport.requiredHint", "The plate and the driver are required; they can be corrected until the load is weighed. Carrier and trailer are optional.")}
          </span>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Cancel")}</button>
            <button
              type="submit"
              className="btn primary"
              disabled={busy || !companyId || !vehiclePlate.trim() || !driverName.trim()}
            >
              {busy ? "…" : t("deliveries.action.announce", "Announce delivery")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={fieldLabel}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

function formatDate(iso: string, locale?: string) {
  return new Date(iso).toLocaleDateString(locale);
}

const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", boxShadow: "var(--shadow-1)", padding: 18,
};
const tbl: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left", padding: "10px 8px", color: "var(--ink-3)", fontWeight: 600,
  borderBottom: "1px solid var(--border)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4,
};
const td: CSSProperties = { padding: "10px 8px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const rowLink: CSSProperties = { color: "var(--ink)", textDecoration: "none", fontWeight: 600 };
const emptyState: CSSProperties = {
  padding: 32, textAlign: "center",
  background: "var(--surface-2)", border: "1px dashed var(--border-strong)", borderRadius: "var(--r-sm)",
  display: "flex", flexDirection: "column", alignItems: "center",
};
const fieldLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const overlay: CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16,
};
const dialog: CSSProperties = {
  width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto",
  background: "var(--surface)", padding: 24, borderRadius: 12,
  boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14,
};
const errBox: CSSProperties = {
  padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13,
};
